import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { TERMS_CHECKBOX_COPY } from "@/components/TermsAcceptanceGate";
import { useAuth } from "@/context/AuthContext";
import { REGISTER } from "@/constants/testIds";
import { toast } from "sonner";
import PasswordStrengthMeter, { scorePassword } from "@/components/PasswordStrengthMeter";
import { isNativeIOS } from "@/lib/nativePush";

export default function RegisterPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [handle, setHandle] = useState("");
  const [age, setAge] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { register } = useAuth();
  const nav = useNavigate();
  // Native iOS shell: external auth would escape the WKWebView into Safari.
  const nativeIOS = isNativeIOS();
  const [params] = useSearchParams();
  const ref = params.get("ref") || null;
  const refCode = params.get("panda_ref") || sessionStorage.getItem("panda_ref") || null;

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const signInWithGoogle = () => {
    try {
      window.sessionStorage.removeItem("pandax_google_intent");
      if (ref) window.sessionStorage.setItem("pandax_ref_handle", ref);
    } catch (err) { console.debug("session-storage unavailable:", err?.name); }
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) return setErr("Password must be at least 8 characters.");
    if (password !== confirm) return setErr("Passwords do not match.");
    if (scorePassword(password).score <= 1) return setErr("Please pick a stronger password — try adding letters, numbers, and a symbol.");
    if (!age) return setErr("You must be 18 or older and accept the Terms of Use, Privacy Policy and Content Policy.");
    setBusy(true);
    const res = await register({ email, password, handle, ageConfirmed: age, ref, refCode });
    if (res?.ok) sessionStorage.removeItem("panda_ref");
    setBusy(false);
    if (res.ok) {
      toast.success("Welcome to the vault.");
      nav("/feed");
    } else {
      setErr(res.error);
    }
  };

  return (
    <div className="grain min-h-screen flex items-center justify-center px-6 py-12 relative">
      <div className="w-full max-w-md relative z-10">
        <Link to="/" className="flex items-center gap-3 mb-10 justify-center">
          <div className="w-10 h-10 rounded-full bg-rose-600 flex items-center justify-center text-lg">🐼</div>
          <span className="font-display text-2xl tracking-tight">My Panda X</span>
        </Link>
        <div className="card-surface p-8">
          <h1 className="font-display text-3xl mb-1">Create your account.</h1>
          <p className="text-sm text-zinc-400 mb-6">Join My Panda X today.</p>
          {ref && (
            <div
              className="mb-6 -mt-2 flex items-center gap-2 text-xs bg-rose-500/10 border border-rose-500/30 text-rose-200 rounded-full px-3 py-2"
              data-testid="register-referrer-chip"
            >
              <span className="w-1.5 h-1.5 bg-rose-400 rounded-full animate-pulse" />
              Invited by <b className="font-semibold">@{ref}</b> · +50 coin welcome bonus
            </div>
          )}

          <Button
            type="button"
            onClick={signInWithGoogle}
            variant="outline"
            className={`w-full rounded-none py-6 bg-transparent border border-white/15 text-white hover:border-rose-600 hover:bg-white/[0.03] items-center justify-center gap-3 ${nativeIOS ? "hidden" : "flex"}`}
            data-testid="register-google-button"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8.1 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 15.2 18.9 12 24 12c3.1 0 5.9 1.2 8.1 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4c-7.7 0-14.3 4.3-17.7 10.7z"/>
              <path fill="#4CAF50" d="M24 44c5.3 0 10.1-2 13.8-5.3l-6.4-5.4C29.5 34.9 26.9 36 24 36c-5.2 0-9.7-3.3-11.3-8l-6.6 5.1C9.5 39.7 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.8l6.4 5.4c-.5.5 6.5-4.8 6.5-15.2 0-1.3-.1-2.4-.4-3.5z"/>
            </svg>
            Continue with Google
          </Button>

          {!nativeIOS && (
          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-xs uppercase tracking-widest text-zinc-500">or</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>
          )}

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Email</Label>
              <Input
                data-testid={REGISTER.emailInput}
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 bg-black/40 border-white/10 rounded-none py-6 focus-visible:ring-rose-600"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Handle (optional)</Label>
              <Input
                data-testid={REGISTER.nameInput}
                type="text"
                placeholder="e.g. panda_lover"
                value={handle}
                onChange={(e) => setHandle(e.target.value)}
                className="mt-2 bg-black/40 border-white/10 rounded-none py-6 focus-visible:ring-rose-600"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Password</Label>
              <Input
                data-testid={REGISTER.passwordInput}
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 bg-black/40 border-white/10 rounded-none py-6 focus-visible:ring-rose-600"
              />
              <PasswordStrengthMeter password={password} testid="register-password-strength" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Confirm password</Label>
              <Input
                data-testid={REGISTER.passwordConfirmInput}
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-2 bg-black/40 border-white/10 rounded-none py-6 focus-visible:ring-rose-600"
              />
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={age}
                onCheckedChange={(v) => setAge(!!v)}
                className="mt-1 border-white/30 data-[state=checked]:bg-rose-600 data-[state=checked]:border-rose-600"
                data-testid="register-age-checkbox"
              />
              <span className="text-sm text-zinc-300 leading-relaxed">
                {TERMS_CHECKBOX_COPY}
              </span>
            </label>
            {err && (
              <div className="text-sm text-rose-500 border-l-2 border-rose-500 pl-3">{err}</div>
            )}
            <Button
              data-testid={REGISTER.submitButton}
              type="submit"
              disabled={busy}
              className="btn-crimson w-full rounded-none py-6 tracking-wide"
            >
              {busy ? "Creating…" : "Create account"}
            </Button>
          </form>

          <div className="mt-8 text-sm text-zinc-400 text-center">
            Already have an account?{" "}
            <Link
              to="/login"
              data-testid={REGISTER.loginLink}
              className="text-rose-500 hover:underline"
            >
              Log in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
